import React, { useState } from 'react';
import { httpStatusCodes } from './Operations/statusCodes';
import Modal from 'react-modal';
import styles from '../../app.module.css'

const PathsForm = ({
    initialData,
    onSave,
    onCancel,
    paths,
    existingTags = [],
    existingParameters = [],
    existingResponses = [],
    existingRequestBody = [],
    existingSecuritySchemes = [],
}) => {
    const [pathName, setPathName] = useState(initialData?.name || '');
    const [operations, setOperations] = useState(Array.isArray(initialData?.operations) ? initialData.operations : []);
    const [currentOperation, setCurrentOperation] = useState(null);
    const [isOperationFormVisible, setIsOperationFormVisible] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(!!initialData);

    const allVerbs = ['get', 'post', 'put', 'delete', 'patch'];

    const [popupMessage, setPopupMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const showPopupModal = (message) => {
        setPopupMessage(message);
        setShowPopup(true);
    }

    const availableVerbs = allVerbs.filter((verb) =>
        currentOperation?.verb === verb || !operations.some((op) => op.verb === verb)
    );

    const handleAddOperation = () => {
        setCurrentOperation(null);
        setIsOperationFormVisible(true);
    };

    const handleEditOperation = (index) => {
        const operation = operations[index];
        const sanitizedOperation = {
            ...operation,
            verb: operation.verb || '',
            // parameters: Array.isArray(operation.parameters)
            //     ? operation.parameters.map((param) => {
            //         if (typeof param === 'string') {
            //             return param;
            //         } else if (param?.$ref) {
            //             return param.$ref.replace('#/components/parameters/', '');
            //         } else {
            //             console.warn('Invalid parameter format:', param);
            //             return '';
            //         }
            //     })
            //     : [],
            parameters: (operation.parameters || []).map((param) =>
                typeof param === 'object' && param.$ref
                    ? param.$ref.replace('#/components/parameters/', '')
                    : param
            ),
            requestBody: operation.requestBody?.$ref
                ? operation.requestBody.$ref.replace('#/components/requestBodies/', '')
                : null,
            responses: Array.isArray(operation.responses)
                ? operation.responses.map(({ httpStatus, response }) => ({
                    httpStatus: httpStatus || '',
                    response: response || '',
                }))
                : [],
        };
        setCurrentOperation({ ...sanitizedOperation, index });
        setIsOperationFormVisible(true);
    };

    const handleDeleteOperation = (index) => {
        setOperations((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSaveOperation = (operation) => {
        if (!operation.verb || !operation.operationId) {
            return showPopupModal('Verb and Operation ID are required.');
        }

        if (
            (operation.verb === 'post' || operation.verb === 'put') &&
            !operation.requestBody &&
            !operation.acknowledgedMissingRequestBody
        ) {
            setModalOpen(true);
            return;

        }
        const responsesArray = Object.entries(operation.responses || {}).map(([httpStatus, responseObj]) => ({
            httpStatus: httpStatus.trim(),
            response: responseObj?.$ref?.replace("#/components/responses/", "").trim() || "",
        }));

        if (!responsesArray.length || responsesArray.some((r) => !r.httpStatus || !r.response)) {
            return showPopupModal("Each response must have both an HTTP status code and a corresponding response.");
        }

        // const formattedParameters = operation.parameters.map((param) => {
        //     if (typeof param === 'object' && param.$ref) {
        //         return param;
        //     } else if (typeof param === 'string') {
        //         return { $ref: `#/components/parameters/${param}` };
        //     } else {
        //         console.warn('Invalid parameter:', param);
        //         return null;
        //     }
        // }).filter(Boolean);

        const formattedParameters = operation.parameters.map((param) =>
            typeof param === 'string'
                ? { $ref: `#/components/parameters/${param}` }
                : param
        );
        const formattedRequestBody =
            typeof operation.requestBody === 'object' && operation.requestBody?.$ref
                ? operation.requestBody
                : typeof operation.requestBody === 'string'
                    ? { $ref: `#/components/requestBodies/${operation.requestBody}` }
                    : null;

        const formattedResponses =
            typeof operation.responses === 'object' && !Array.isArray(operation.responses)
                ? operation.responses
                : Array.isArray(operation.responses)
                    ? operation.responses.reduce((acc, { httpStatus, response }) => {
                        if (httpStatus && response) {
                            acc[httpStatus] = { $ref: `#/components/responses/${response}` };
                        } else {
                            console.warn('Invalid response:', { httpStatus, response });
                        }
                        return acc;
                    }, {})
                    : {};


        const formattedOperation = {
            ...operation,
            parameters: formattedParameters,
            requestBody: formattedRequestBody,
            responses: formattedResponses,
        };

        if (currentOperation?.index >= 0) {
            setOperations((prev) => {
                const updated = [...prev];
                updated[currentOperation.index] = { ...formattedOperation };
                return updated;
            });
        } else {
            setOperations((prev) => [...prev, { ...formattedOperation }]);
        }

        setIsOperationFormVisible(false);
        setCurrentOperation(null);
        setModalOpen(false);
    };

    const handleSavePath = () => {
        if (!pathName) {
            return showPopupModal('Path name is required.');
        }

        if (!Object.keys(operations || {}).length) {
            return showPopupModal('At least one operation must be defined for the path.');
        }

        const pathExists = Object.keys(paths).includes(pathName);

        if (!isEditMode && pathExists) {
            return showPopupModal(`Path "${pathName}" already exists. Please use a different name.`);
        }

        const formattedOperations = operations.map((op) => {
            const formattedResponses = typeof op.responses === 'object'
                ? op.responses
                : Array.isArray(op.responses)
                    ? op.responses.reduce((acc, resp) => {
                        if (resp.httpStatus && resp.response) {
                            acc[resp.httpStatus] = { $ref: `#/components/responses/${resp.response}` };
                        } else {
                            console.warn('Invalid response during path save:', resp);
                        }
                        return acc;
                    }, {})
                    : {};
            return {
                ...op,
                responses: formattedResponses,
            };
        });

        onSave(pathName, formattedOperations);
    };

    const handleCancelOperation = () => {
        setIsOperationFormVisible(false);
        setCurrentOperation(null);
    };

    return (
        <div className={styles.mainContainer}>
            <div className={styles.style2}>
                <h3>{initialData ? 'Edit Path' : 'Add Path'}</h3>
                <div className={styles.style3}>
                    <label>Path:<span className={styles.requiredField}>*</span></label>
                    <input
                        type="text"
                        value={pathName}
                        onChange={(e) => setPathName(e.target.value)}
                        placeholder="Enter Path Name"
                        className={styles.textBox}
                        disabled={!!initialData}
                    />
                </div>
                <h4>Operations</h4>
                {operations.map((operation, index) => (
                    <div
                        key={index}
                        className={styles.mainContainer}
                    >
                        <div style={{ fontWeight: 'bold' }}>{operation.verb.toUpperCase()}</div>
                        <div>{operation.summary}</div>
                        <div className={styles.actionButtonsContainer} >
                            <button
                                onClick={() => handleEditOperation(index)}
                                className={styles.editButton}
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDeleteOperation(index)}
                                className={styles.deleteButton}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}

                <button
                    onClick={handleAddOperation}
                    className={styles.propertyButton}
                >
                    + Add Operation
                </button>

                {isOperationFormVisible && (
                    <OperationForm
                        initialData={currentOperation}
                        availableVerbs={availableVerbs}
                        existingTags={existingTags}
                        existingParameters={existingParameters}
                        existingRequestBody={existingRequestBody}
                        existingSecuritySchemes={existingSecuritySchemes}
                        existingResponses={existingResponses}
                        onSave={handleSaveOperation}
                        onCancel={handleCancelOperation}
                        paths={paths}
                        operations={operations}
                        isModalOpen={isModalOpen}
                        setModalOpen={setModalOpen}
                        setCurrentOperation={setCurrentOperation}
                        showPopupModal={showPopupModal}
                        showPopup={showPopup}
                        popupMessage={popupMessage}
                        setShowPopup={setShowPopup}
                    />
                )}

                <div className={styles.sNcButtonContainer}>
                    <button
                        onClick={handleSavePath}
                        className={styles.saveButton}
                    >
                        {isEditMode ? 'Update Path' : 'Save Path'}
                    </button>
                    <button
                        onClick={onCancel}
                        className={styles.cancelButton}
                    >
                        Cancel
                    </button>
                </div>
                {showPopup && (
                    <div className={styles.popupOverlay}>
                        <div className={styles.popup}>
                            <h3>Error</h3>
                            <p>{popupMessage}</p>
                            <button onClick={() => setShowPopup(false)} className="popup-close-button">
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const OperationForm = ({ initialData, availableVerbs, onSave, onCancel, existingTags, existingParameters, existingRequestBody, existingResponses, existingSecuritySchemes, paths, operations, isModalOpen, setModalOpen, setCurrentOperation, showPopupModal, showPopup, popupMessage, setShowPopup }) => {

    const [operation, setOperation] = useState(() => {
        const defaultOperation = {
            verb: '',
            operationId: '',
            summary: '',
            description: '',
            tags: [],
            parameters: [],
            requestBody: '',
            responses: [],
            security: [],
            deprecated: false,
            ...initialData,
        };

        const initializedOperation = {
            ...defaultOperation,
            ...initialData,
            parameters: Array.isArray(initialData?.parameters) ? initialData.parameters : [],
            responses: Array.isArray(initialData?.responses) ? initialData.responses : [],
            verb: initialData?.verb || '',
            requestBody: initialData?.requestBody || '',
        };

        return initializedOperation;
    });

    const [operationIdError, setOperationIdError] = useState('');

    const usedStatusCodes = operation.responses.map((resp) => resp.httpStatus);

    const availableStatusCodes = httpStatusCodes.filter(
        (status) => !usedStatusCodes.includes(status.code)
    );

    const handleInputChange = (field, value) => {
        if (field === 'operationId') {

            const allSavedOperationIds = Object.values(paths || {})
                .flatMap((operations) => Object.values(operations))
                .map((op) => op.operationId);

            const allUnsavedOperationIds = operations
                .filter((op) => op.operationId !== initialData?.operationId)
                .map((op) => op.operationId);

            const allOperationIds = [...allSavedOperationIds, ...allUnsavedOperationIds];
            if (allOperationIds.includes(value)) {
                setOperationIdError(`Operation ID "${value}" must be unique.`);
            } else {
                setOperationIdError('');
            }
        }

        setOperation((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        if (!operation.verb || !operation.operationId) {
            showPopupModal('Verb and Operation ID are required.');
            return;
        }

        if (operationIdError) {
            showPopupModal(operationIdError);
            return;
        }

        const formattedParameters = operation.parameters.map((param) => ({
            $ref: `#/components/parameters/${param}`,
        }));

        const formattedResponses = operation.responses.reduce((acc, resp) => {
            if (resp.httpStatus) {
                acc[resp.httpStatus] = { $ref: `#/components/responses/${resp.response}` };
            }
            return acc;
        }, {});

        onSave({ ...operation, parameters: formattedParameters, responses: formattedResponses });
    };

    const handleModalChoice = () => {
        setOperation((prev) => ({
            ...prev,
            acknowledgedMissingRequestBody: true,
        }));
        setModalOpen(false);
    };

    const handleCheckboxChange = (field, value, isChecked) => {
        setOperation((prev) => ({
            ...prev,
            [field]: isChecked
                ? [...(prev[field] || []), value]
                : (prev[field] || []).filter((item) => item !== value),
        }));
    };

    const handleResponseChange = (index, field, value) => {
        setOperation((prev) => {
            const updatedResponses = [...(prev.responses || [])];
            if (!updatedResponses[index]) {
                updatedResponses[index] = { httpStatus: '', response: '' };
            }
            updatedResponses[index][field] = value;
            return { ...prev, responses: updatedResponses };
        });
    };

    const addResponse = () => {
        setOperation((prev) => ({
            ...prev,
            responses: [...(prev.responses || []), { httpStatus: '', response: '' }],
        }));
    };

    const removeResponse = (index) => {
        setOperation((prev) => ({
            ...prev,
            responses: prev.responses.filter((_, i) => i !== index),
        }));
    };

    return (
        <div className={styles.style2}>
            <h4>{initialData ? 'Edit Operation' : 'Add Operation'}</h4>
            <div className={styles.style3}>
                <label>Verb:<span className={styles.requiredField}>*</span></label>
                <select
                    value={operation.verb}
                    onChange={(e) => handleInputChange('verb', e.target.value)}
                    className={styles.dropdown}
                >
                    <option value="">Select Verb</option>
                    {availableVerbs.map((verb) => (
                        <option key={verb} value={verb}>
                            {verb.toUpperCase()}
                        </option>
                    ))}
                </select>
            </div>
            <div className={styles.style3}>
                <label>Operation ID:<span className={styles.requiredField}>*</span></label>
                <input
                    type="text"
                    value={operation.operationId}
                    onChange={(e) => handleInputChange('operationId', e.target.value)}
                    placeholder="Enter Operation ID"
                    className={styles.textBox}
                />
            </div>
            <div className={styles.style3}>
                <label>Summary:</label>
                <input
                    type="text"
                    value={operation.summary}
                    onChange={(e) => handleInputChange('summary', e.target.value)}
                    placeholder="Enter Summary"
                    className={styles.textBox}
                />
            </div>
            <div className={styles.style3}>
                <label>Description:</label>
                <textarea
                    value={operation.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter Description"
                    className={styles.textBox}
                />
            </div>


            <div className={styles.style3}>
                <label><strong>Tags:</strong></label>
                <div className={styles.checkBoxcontainer}>
                    {existingTags && existingTags.length > 0 ? (
                        existingTags.map((tag, index) => (
                            <label key={index} className={styles.checkBoxContents}>
                                <input
                                    type="checkbox"
                                    checked={operation.tags.includes(tag.name)}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'tags',
                                            tag.name,
                                            e.target.checked
                                        )
                                    }
                                />
                                {tag.name}
                            </label>
                        ))
                    ) : (
                        <p>No predefined Tags available.</p>
                    )}
                </div>
            </div>


            <div className={styles.style3}>
                <label><strong>Parameters:</strong></label>
                <div className={styles.checkBoxcontainer}>
                    {existingParameters && existingParameters.length > 0 ? (
                        existingParameters.map((param) => (
                            <label key={param} className={styles.checkBoxContents}>
                                <input
                                    type="checkbox"
                                    checked={operation.parameters.includes(param)}
                                    onChange={(e) =>
                                        handleCheckboxChange('parameters', param, e.target.checked)
                                    }
                                />
                                {param}
                            </label>
                        ))
                    ) : (
                        <p>No predefined Parameters available.</p>
                    )}
                </div>
            </div>


            <div className={styles.style3}>
                <label>Request Body:</label>
                <select
                    value={operation.requestBody || ''}
                    onChange={(e) => handleInputChange('requestBody', e.target.value)}
                    disabled={operation.verb.toLowerCase() === 'get' || operation.verb.toLowerCase() === 'delete'}
                    className={styles.dropdown}
                >
                    <option value="">Select Request Body</option>
                    {existingRequestBody.map((body) => (
                        <option key={body} value={body}>
                            {body}
                        </option>
                    ))}
                </select>
                <Modal isOpen={isModalOpen} onRequestClose={() => setModalOpen(false)} style={{
                    content: {
                        width: "320px",
                        height: "150px",
                        margin: "auto",
                        padding: "20px",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "center",
                        textAlign: "center",
                    },
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                }}>
                    <p style={{ fontSize: "16px", marginBottom: "20px", fontWeight: "bold", color: "red" }}>
                        {operation.verb.toUpperCase()} operation missing request body.
                    </p>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <button
                            onClick={() => handleModalChoice("ignore")}
                            className={styles.acknowledgeButton}
                        >
                            Acknowledge
                        </button>
                    </div>
                </Modal>
            </div>


            <div className={styles.style3}>
                <label><strong>Responses:<span className={styles.requiredField}>*</span></strong></label>
                {operation.responses.map((response, index) => (
                    <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', marginTop: '10px' }}>
                        <select
                            value={response.httpStatus}
                            onChange={(e) => handleResponseChange(index, 'httpStatus', e.target.value)}
                            className={styles.dropdown}
                        >
                            <option value="">Select HTTP Status</option>
                            {[
                                ...operation.responses.map((resp) => ({
                                    code: resp.httpStatus,
                                    description:
                                        httpStatusCodes.find((status) => status.code === resp.httpStatus)?.description,
                                })),
                                ...availableStatusCodes,
                            ]
                                .filter((status, index, self) =>
                                    self.findIndex((s) => s.code === status.code) === index
                                )
                                .filter(
                                    (status) =>
                                        !operation.responses.some((resp) =>
                                            resp.httpStatus === status.code && resp.httpStatus !== response.httpStatus
                                        )
                                )
                                .map((status) => (
                                    <option key={status.code} value={status.code}>
                                        {`${status.code} - ${status.description}`}
                                    </option>
                                ))}
                        </select>
                        <select
                            value={response.response}
                            onChange={(e) => handleResponseChange(index, 'response', e.target.value)}
                            className={styles.dropdown}
                        >
                            <option value="">Select Response</option>
                            {existingResponses.map((resp) => (
                                <option key={resp} value={resp}>
                                    {resp}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => removeResponse(index)}
                            style={{
                                padding: '8px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                height: '35px',
                                marginTop: '10px',
                            }}
                        >
                            Delete
                        </button>
                    </div>
                ))}
                <button
                    onClick={addResponse}
                    className={styles.propertyButton}
                >
                    + Add Response
                </button>
            </div>


            <div className={styles.style3}>
                <label><strong>Security:</strong></label>
                <div className={styles.checkBoxcontainer}>
                    {existingSecuritySchemes && Object.keys(existingSecuritySchemes).length > 0 ? (
                        Object.keys(existingSecuritySchemes).map((scheme) => {
                            const schemeType = existingSecuritySchemes[scheme].type;
                            const isOauth2 = schemeType === 'oauth2';


                            const selectedScheme =
                                Array.isArray(operation.security) &&
                                operation.security.find((sec) => Object.keys(sec).includes(scheme));

                            const selectedScopes = selectedScheme?.[scheme] || [];

                            const currentScopes = existingSecuritySchemes[scheme]?.flows?.[Object.keys(existingSecuritySchemes[scheme]?.flows || {})[0]]?.scopes || {};

                            return (
                                <div key={scheme} className={styles.checkBoxcontainer}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={!!selectedScheme}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;

                                                setOperation((prevOperation) => {
                                                    const updatedSecurity = Array.isArray(prevOperation.security)
                                                        ? [...prevOperation.security]
                                                        : [];
                                                    const schemeIndex = updatedSecurity.findIndex((sec) =>
                                                        Object.keys(sec).includes(scheme)
                                                    );

                                                    if (isChecked) {
                                                        if (schemeIndex === -1) {
                                                            updatedSecurity.push({ [scheme]: isOauth2 ? [] : [] });
                                                        }
                                                    } else {
                                                        if (schemeIndex !== -1) {
                                                            updatedSecurity.splice(schemeIndex, 1);
                                                        }
                                                    }

                                                    return {
                                                        ...prevOperation,
                                                        security: updatedSecurity,
                                                    };
                                                });
                                            }}
                                        />
                                        {`${scheme} (${schemeType})`}
                                    </label>


                                    {isOauth2 && selectedScheme && (
                                        <div style={{ paddingLeft: '20px', marginTop: '5px' }}>
                                            {Object.entries(currentScopes).map(
                                                ([scopeKey, scopeValue]) => (
                                                    <label key={scopeKey} className={styles.labelStyle}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedScopes.includes(`${scopeKey}: ${scopeValue}`)}
                                                            onChange={(e) => {
                                                                const isChecked = e.target.checked;

                                                                setOperation((prevOperation) => {
                                                                    const updatedSecurity = Array.isArray(prevOperation.security)
                                                                        ? [...prevOperation.security]
                                                                        : [];
                                                                    const schemeIndex = updatedSecurity.findIndex((sec) =>
                                                                        Object.keys(sec).includes(scheme)
                                                                    );

                                                                    if (schemeIndex !== -1) {
                                                                        let scopes = updatedSecurity[schemeIndex][scheme] || [];
                                                                        if (isChecked) {
                                                                            if (!scopes.includes(`${scopeKey}: ${scopeValue}`)) {
                                                                                scopes.push(`${scopeKey}: ${scopeValue}`);
                                                                            }
                                                                        } else {
                                                                            scopes = scopes.filter(
                                                                                (s) => s !== `${scopeKey}: ${scopeValue}`
                                                                            );
                                                                        }
                                                                        updatedSecurity[schemeIndex][scheme] = scopes;
                                                                    }

                                                                    return {
                                                                        ...prevOperation,
                                                                        security: updatedSecurity,
                                                                    };
                                                                });
                                                            }}
                                                        />
                                                        {`${scopeKey}: ${scopeValue}`}
                                                    </label>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <p>No predefined Security Schemes available.</p>
                    )}
                </div>
            </div>

            <div className={styles.checkBoxContents}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'red', }}>
                    <input
                        type="checkbox"
                        checked={operation.deprecated || false}
                        onChange={(e) => handleInputChange('deprecated', e.target.checked)}
                    />
                    Deprecated
                </label>
            </div>

            <div className={styles.sNcButtonContainer}>
                <button
                    onClick={handleSubmit}
                    className={styles.saveButton}
                >
                    Save Operation
                </button>
                <button
                    onClick={onCancel}
                    className={styles.cancelButton}
                >
                    Cancel
                </button>
            </div>
            {showPopup && (
                <div className={styles.popupOverlay}>
                    <div className={styles.popup}>
                        <h3>Error</h3>
                        <p>{popupMessage}</p>
                        <button onClick={() => setShowPopup(false)} className="popup-close-button">
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PathsForm;
